import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { MusicNote, Microphone, Stop, X, ArrowLeft, ArrowRight, Scissors, Hash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Editor — post-capture screen shown after chunked-video processing finishes.
 * Routed as /editor/:postId. Loads an existing draft post (created immediately
 * after upload completes) and lets the creator:
 *   - Trim the video (server-side stream-copy, no re-encode)
 *   - Pick a sound from their saved library (mixes on top of original audio)
 *   - Record a voiceover (MediaRecorder → base64 → /api/sounds/voiceover)
 *   - Edit caption + hashtags + category + privacy
 *   - Continue → payment page
 */
const CATEGORY_OPTIONS = [
  "Entertainment", "Education", "Lifestyle", "Comedy", "Music", "Sports", "News", "Fashion", "Food",
];
const HASHTAG_SUGGESTIONS = ["fyp", "viral", "trending", "funny", "music", "dance", "food", "love"];

function extractHashtags(text) {
  if (!text) return [];
  const set = new Set();
  const matches = (text || "").match(/#([A-Za-z0-9_]{1,40})/g) || [];
  matches.forEach((m) => set.add(m.slice(1).toLowerCase()));
  return Array.from(set);
}

export default function Editor() {
  const { postId } = useParams();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("");
  const [privacy, setPrivacy] = useState("public");
  const [sounds, setSounds] = useState([]);
  const [pickedSoundId, setPickedSoundId] = useState("");
  const [pickedSoundUrl, setPickedSoundUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [soundVolume, setSoundVolume] = useState([70]); // 0-100
  const [recording, setRecording] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  // Trim state (seconds)
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [applyingTrim, setApplyingTrim] = useState(false);

  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecRef = useRef(null);
  const recChunksRef = useRef([]);

  // Load the draft post, polling until it's ready.
  const loadPost = useCallback(async () => {
    try {
      const { data } = await api.get(`/posts/${postId}/detail`);
      setPost(data);
      if (data.status !== "draft") {
        // Already past editing — bounce to payment or feed.
        if (data.status === "pending_payment") navigate(`/pay/${postId}`, { replace: true });
        else if (data.status === "live") navigate(`/feed`, { replace: true });
      }
      return data;
    } catch (e) {
      toast.error("Post not found");
      navigate("/post");
      return null;
    }
  }, [postId, navigate]);

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      const p = await loadPost();
      if (!p || stop) return;
      const busy = p.processing_status !== "ready" && p.processing_status !== "failed";
      if (busy) setTimeout(tick, 2500);
    };
    tick();
    return () => { stop = true; };
  }, [loadPost]);

  // When we first receive a fresh post, hydrate editable fields (only if we haven't
  // touched them yet — avoid clobbering user edits when the poll refreshes progress).
  useEffect(() => {
    if (!post) return;
    setCaption((prev) => (prev ? prev : (post.caption || "")));
    setCategory((prev) => (prev ? prev : (post.category || "")));
    setPrivacy((prev) => (prev !== "public" ? prev : (post.privacy || "public")));
    if (post.sound_id && !pickedSoundId) {
      setPickedSoundId(post.sound_id);
      setPickedSoundUrl(post.sound_audio_url || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.post_id]);

  // Load sound library
  useEffect(() => {
    api.get("/sounds/mine").then(({ data }) => setSounds(data)).catch(() => {});
  }, []);

  // Sync overlay audio with the <video> element
  useEffect(() => {
    const v = videoRef.current, a = audioRef.current;
    if (!v || !a || !pickedSoundUrl) return;
    const onPlay = () => { a.currentTime = v.currentTime; a.play().catch(() => {}); };
    const onPause = () => a.pause();
    const onSeek = () => { a.currentTime = v.currentTime; };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("seeked", onSeek);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("seeked", onSeek);
    };
  }, [pickedSoundUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = (soundVolume[0] || 0) / 100;
  }, [soundVolume]);

  const onVideoMeta = () => {
    const v = videoRef.current;
    if (!v || !isFinite(v.duration)) return;
    setDuration(v.duration);
    // Initialise trim range to full clip only the first time
    if (trimEnd === 0) setTrimEnd(v.duration);
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      recChunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && recChunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recChunksRef.current, { type: rec.mimeType });
        const b64 = await new Promise((resolve) => {
          const fr = new FileReader();
          fr.onload = () => resolve(fr.result);
          fr.readAsDataURL(blob);
        });
        setUploadingVoice(true);
        try {
          const { data } = await api.post("/sounds/voiceover", {
            audio_base64: b64,
            mime_type: rec.mimeType || "audio/webm",
            title: `Voiceover · ${new Date().toLocaleTimeString()}`,
          });
          setSounds((s) => [data, ...s]);
          setPickedSoundId(data.sound_id);
          setPickedSoundUrl(data.audio_url);
          toast.success("Voiceover saved");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Voiceover upload failed");
        } finally {
          setUploadingVoice(false);
        }
      };
      mediaRecRef.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };
  const stopRecord = () => {
    mediaRecRef.current?.stop();
    setRecording(false);
  };

  const insertTag = (t) => {
    setCaption((c) => `${c}${c && !c.endsWith(" ") ? " " : ""}#${t}`);
  };

  // Persist the current edits as a draft PATCH (no trim)
  const saveDraft = async () => {
    await api.patch(`/posts/${postId}/draft`, {
      caption: caption.trim(),
      category: category || null,
      privacy,
      sound_id: pickedSoundId || "",
    });
  };

  const applyTrim = async () => {
    if (!duration) return;
    // Ignore trivial trims (< 0.5s off either end)
    const trimmedAtAll = trimStart > 0.5 || (duration - trimEnd) > 0.5;
    if (!trimmedAtAll) {
      toast.info("Nothing to trim");
      return;
    }
    setApplyingTrim(true);
    try {
      await api.patch(`/posts/${postId}/draft`, {
        trim_start: trimStart,
        trim_end: trimEnd,
      });
      toast.success("Trim applied — video re-processing…");
      // Force a fresh poll cycle
      const p = await loadPost();
      if (p?.processing_status === "trimming") {
        // Poll until processing_status flips back to ready
        const wait = () => new Promise((r) => setTimeout(r, 2000));
        for (let i = 0; i < 60; i++) {
          // eslint-disable-next-line no-await-in-loop
          await wait();
          // eslint-disable-next-line no-await-in-loop
          const fresh = await loadPost();
          if (fresh?.processing_status === "ready" || fresh?.processing_status === "failed") break;
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Trim failed");
    } finally {
      setApplyingTrim(false);
    }
  };

  const goToPay = async () => {
    if (!isReady) {
      toast.error("Video is still processing");
      return;
    }
    setSaving(true);
    try {
      await saveDraft();
      const { data } = await api.post(`/posts/${postId}/submit`);
      if (data.status === "pending_payment") navigate(`/pay/${postId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to continue");
    } finally {
      setSaving(false);
    }
  };

  const tags = extractHashtags(caption);
  const suggestions = HASHTAG_SUGGESTIONS.filter((s) => !tags.includes(s)).slice(0, 6);
  const isReady = post?.processing_status === "ready";
  const videoUrl = post?.media_url;
  const progress = post?.processing_progress || 0;
  const stage = post?.processing_stage || (post?.processing_status === "trimming" ? "Trimming" : "Processing");

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="editor-page">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="editor-back-btn"
          className="text-white/70 hover:text-white"
        >
          <ArrowLeft size={20} weight="bold" />
        </Button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black">Edit your video</h1>
          <p className="text-white/50 text-sm">Trim, add a sound, write a caption. Then Continue to payment.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* LEFT — Preview + Trim */}
        <div className="space-y-3">
          <div className="aspect-[9/16] bg-black rounded-2xl overflow-hidden relative">
            {isReady && videoUrl ? (
              <video
                key={videoUrl}
                ref={videoRef}
                src={videoUrl}
                controls
                playsInline
                onLoadedMetadata={onVideoMeta}
                className="w-full h-full object-contain"
                data-testid="editor-video"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center p-6">
                <div className="w-12 h-12 border-4 border-blue-400/40 border-t-blue-400 rounded-full animate-spin" />
                <p className="font-heading font-bold">{stage}</p>
                <p className="text-xs text-white/50">{progress}%</p>
              </div>
            )}
          </div>
          {pickedSoundUrl && <audio ref={audioRef} src={pickedSoundUrl} preload="auto" />}

          {/* Trim */}
          {isReady && duration > 0 && (
            <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Scissors size={18} weight="fill" className="text-emerald-400" />
                <span className="font-heading font-bold">Trim</span>
                <span className="ml-auto text-xs text-white/50">
                  {trimStart.toFixed(1)}s → {trimEnd.toFixed(1)}s
                </span>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Start ({trimStart.toFixed(1)}s)</label>
                <Slider
                  data-testid="editor-trim-start"
                  value={[trimStart]}
                  onValueChange={(v) => setTrimStart(Math.min(v[0], trimEnd - 0.5))}
                  min={0}
                  max={duration}
                  step={0.1}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">End ({trimEnd.toFixed(1)}s)</label>
                <Slider
                  data-testid="editor-trim-end"
                  value={[trimEnd]}
                  onValueChange={(v) => setTrimEnd(Math.max(v[0], trimStart + 0.5))}
                  min={0}
                  max={duration}
                  step={0.1}
                  className="mt-1"
                />
              </div>
              <Button
                onClick={applyTrim}
                disabled={applyingTrim || post?.processing_status === "trimming"}
                data-testid="editor-trim-apply"
                variant="outline"
                className="w-full border-emerald-500/40 bg-transparent hover:bg-emerald-500/10 text-emerald-300 gap-2"
              >
                <Scissors size={14} weight="bold" />
                {applyingTrim || post?.processing_status === "trimming" ? "Applying trim…" : "Apply trim"}
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT — Metadata + Sound */}
        <div className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50">Caption</label>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Write something…"
              maxLength={280}
              data-testid="editor-caption"
              className="bg-white/5 border-white/10 mt-1 min-h-24"
            />
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    data-testid={`editor-hashtag-suggest-${s}`}
                    onClick={() => insertTag(s)}
                    className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs hover:bg-white/10 flex items-center gap-1"
                  >
                    <Hash size={12} weight="bold" /> {s}
                  </button>
                ))}
              </div>
            )}
            {tags.length > 0 && (
              <p className="text-[11px] text-blue-300/80 mt-2">{tags.map((t) => `#${t}`).join(" ")}</p>
            )}
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <MusicNote size={20} weight="fill" className="text-blue-400" />
              <span className="font-heading font-bold">Sound</span>
              {pickedSoundId && (
                <button
                  onClick={() => { setPickedSoundId(""); setPickedSoundUrl(""); }}
                  className="ml-auto text-white/50 hover:text-white text-xs flex items-center gap-1"
                  data-testid="editor-sound-clear"
                >
                  <X size={12} /> Remove
                </button>
              )}
            </div>
            <Select
              value={pickedSoundId}
              onValueChange={(v) => {
                setPickedSoundId(v);
                const s = sounds.find((x) => x.sound_id === v);
                setPickedSoundUrl(s?.audio_url || "");
              }}
            >
              <SelectTrigger data-testid="editor-sound-picker" className="bg-white/5 border-white/10">
                <SelectValue placeholder={sounds.length ? "Pick from your saved sounds" : "No saved sounds yet — tap 'Use sound' on any feed video to save one"} />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10 text-white max-h-72">
                {sounds.map((s) => (
                  <SelectItem key={s.sound_id} value={s.sound_id} data-testid={`sound-opt-${s.sound_id}`}>
                    {s.is_voiceover ? "🎙 " : "🎵 "}{s.title || s.sound_id}
                    {s.source_creator_name && ` · @${s.source_creator_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {pickedSoundUrl && (
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/50">Sound volume: {soundVolume[0]}%</label>
                <Slider value={soundVolume} onValueChange={setSoundVolume} max={100} step={1} className="mt-1" />
              </div>
            )}

            <div className="pt-2 border-t border-white/10">
              {!recording ? (
                <Button
                  data-testid="editor-record-btn"
                  onClick={startRecord}
                  disabled={uploadingVoice}
                  variant="outline"
                  className="border-red-500/40 bg-transparent hover:bg-red-500/10 text-red-300 w-full gap-2"
                >
                  <Microphone size={16} weight="fill" />
                  {uploadingVoice ? "Saving voiceover…" : "Record voiceover"}
                </Button>
              ) : (
                <Button
                  data-testid="editor-record-stop"
                  onClick={stopRecord}
                  className="bg-red-600 hover:bg-red-700 w-full gap-2 animate-pulse"
                >
                  <Stop size={16} weight="fill" /> Stop recording
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Category</label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="editor-category" className="bg-white/5 border-white/10 mt-1">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50">Privacy</label>
              <Select value={privacy} onValueChange={setPrivacy}>
                <SelectTrigger data-testid="editor-privacy" className="bg-white/5 border-white/10 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10 text-white">
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="followers">Followers only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            data-testid="editor-continue-btn"
            onClick={goToPay}
            disabled={saving || !isReady || post?.processing_status === "trimming"}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 font-heading font-black gap-2"
          >
            <ArrowRight size={18} weight="bold" />
            {saving ? "Saving…" : isReady ? "Continue to payment" : "Waiting for processing…"}
          </Button>
        </div>
      </div>
    </div>
  );
}
